import type { OnDestroy, OnInit } from '@angular/core';
import { Component, HostBinding, Input } from '@angular/core';
import { TypedComponentRendering } from '@core/jss-models';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import type { CommodityOfferContent } from '@innogy/commodity-offers';
import {
  HorizontalCommodityOfferComponent,
  MixedCommodityOfferComponent,
  scToCommodityOfferContentMapper,
  VerticalCommodityOfferComponent,
} from '@innogy/commodity-offers';
import { Store } from '@ngrx/store';
import {
  callGetOffersAction,
  readyToLoadOffers,
  setBACFunnelStepAction,
  showAomModalAction,
  showOfferAction,
  choosePropositionAction,
  setFeatureFlagsAction,
} from '@innogy/become-a-customer/state';
import { Subject, filter, takeUntil, withLatestFrom } from 'rxjs';
import { openMoreInfo } from '@innogy/become-a-customer/shared';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { JssPlatformService } from '@core/jss-platform';
import type { ColumnContext } from '@legacy/common-ui-shared-interfaces';
import { CommonModule } from '@angular/common';
import { OfferMailer } from '@innogy/offer-saver';
import { GenericLightboxModule } from '@innogy/common-ui-modals';
import { JssModule } from '@sitecore-jss/sitecore-jss-angular';
import {
  getContentSectionId,
  getContentSectionTheme,
  ContentSectionComponent,
  type ContentSectionTheme,
} from '@core/content-section';

import type { AcquisitionCommodityOfferFields } from './acquisition-commodity-offer.model';
import { acquisitionCommodityOfferVM } from './acquisition-commodity-offer.selector';
import { openAdjustCalculationModal } from '../utils/adjust-calculation-modal';

@Component({
  selector: 'wl-acquisition-commodity-offer-container',
  templateUrl: './acquisition-commodity-offer-container.component.html',
  styleUrls: [
    './acquisition-commodity-offer-container.component.ed.scss',
    './acquisition-commodity-offer-container.component.essent.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    HorizontalCommodityOfferComponent,
    VerticalCommodityOfferComponent,
    MixedCommodityOfferComponent,
    GenericLightboxModule,
    JssModule,
    ContentSectionComponent,
  ],
  providers: [JssPlatformService, OfferMailer],
})
export class AcquisitionCommodityOfferContainerComponent
  implements OnInit, OnDestroy
{
  @HostBinding('id')
  public id = 'vertical-offer';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering!: TypedComponentRendering<AcquisitionCommodityOfferFields>;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() context?: ColumnContext;

  sectionId?: string;
  theme?: ContentSectionTheme;

  viewModel$ = this.store$.select(acquisitionCommodityOfferVM);
  content?: CommodityOfferContent;

  private readonly readyToLoadOffers$ = this.store$.select(readyToLoadOffers);

  readonly onDestroy$ = new Subject();

  constructor(
    private readonly store$: Store,
    private readonly modalService: NgbModal,
    protected readonly jssPlatformService: JssPlatformService
  ) {}

  ngOnInit(): void {
    if (this.rendering) {
      this.content = scToCommodityOfferContentMapper(this.rendering);
      this.sectionId = getContentSectionId(this.rendering);
      this.theme = getContentSectionTheme(this.rendering);
    }
    this.store$.dispatch(showOfferAction());
    this.readyToLoadOffers$
      .pipe(
        filter((status) => !!status),
        withLatestFrom(this.viewModel$),
        takeUntil(this.onDestroy$)
      )
      .subscribe(
        ([
          ,
          {
            funnelSettings: { offerset },
          },
        ]) => {
          this.store$.dispatch(
            callGetOffersAction({
              offerSet: offerset,
            })
          );
        }
      );

    this.store$.dispatch(setBACFunnelStepAction({ step: 'Offer' }));

    this.initFeatureFlags();
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  selectOffer = (offer: Offer) =>
    this.store$.dispatch(choosePropositionAction({ offer }));

  adjustCalculation = () =>
    openAdjustCalculationModal(this.modalService, this.store$, this.rendering);

  openMoreInfo = (offer: Offer) =>
    openMoreInfo(this.store$, offer, () =>
      this.store$.dispatch(choosePropositionAction({ offer }))
    );

  viewTariffs = (offer: Offer) =>
    this.store$.dispatch(
      showAomModalAction({
        offer,
        showFooter: true,
        rendering: this.rendering,
      })
    );

  initFeatureFlags() {
    this.store$.dispatch(
      setFeatureFlagsAction({
        featureFlagId: 'termijnbedragVariantA',
        isEnabled: !!this.rendering?.fields.TermijnbedragVariantA.value,
      })
    );

    this.store$.dispatch(
      setFeatureFlagsAction({
        featureFlagId: 'termijnbedragVariantB',
        isEnabled: !!this.rendering?.fields.TermijnbedragVariantB.value,
      })
    );
  }

  /**
   * If the component has context it means it is loaded within a dynamic row.
   * Dynamic rows allow content editors to upload a background image.
   *
   * When migrating to accessible templates we move away from dynamic rows to content sections.
   * For accessibility reasons, we no longer allow background images to be uploaded in these new sections.
   *
   * However, for this component an exception has been made: an experiment showed
   * that having a background image leads to a higher conversion rate.
   * Therefore, a `BackgroundImage` prop was added to the rendering.
   */
  get backgroundStyles(): Record<string, string> | null {
    if (!this.rendering?.fields?.BackgroundImage?.value?.src) {
      return null;
    }

    return {
      'background-image': `url('${this.rendering.fields.BackgroundImage.value.src}')`,
      'background-size': 'cover',
      'background-position': 'center bottom',
      'background-repeat': 'no-repeat',
    };
  }
}
